import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Calendar, Clock, X, Users, Eye } from "lucide-react";
import { ReservationReceiptModal } from "@/components/reservations/ReservationReceiptModal";
import { useGymReservations } from "@/hooks/reservations/useGymReservations";
import { useCancelReservation } from "@/hooks/reservations/useCancelReservation";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useState, useEffect } from "react";
import { Textarea } from "@/components/ui/textarea";
import { useGymReservationMessage } from "@/hooks/reservations/useGymReservationMessage";
import { useUpdateGymReservationMessage } from "@/hooks/reservations/useUpdateGymReservationMessage";

interface ReservationOverviewProps {
  gymId: string;
}

export const ReservationOverview = ({ gymId }: ReservationOverviewProps) => {
  const { data: reservations, isLoading } = useGymReservations(gymId);
  const { data: currentMessage, isLoading: isLoadingMessage } = useGymReservationMessage(gymId);
  const cancelReservation = useCancelReservation();
  const updateMessage = useUpdateGymReservationMessage();
  const [selectedReservation, setSelectedReservation] = useState<any>(null);
  const [message, setMessage] = useState("");
  const [receiptModal, setReceiptModal] = useState<{
    isOpen: boolean;
    receiptUrl: string | null;
    title: string;
  }>({
    isOpen: false,
    receiptUrl: null,
    title: ''
  });

  // Update local message when data loads
  useEffect(() => {
    if (currentMessage !== undefined) {
      setMessage(currentMessage);
    }
  }, [currentMessage]);

  if (isLoading || isLoadingMessage) {
    return <div className="text-center py-8">Cargando reservas...</div>;
  }

  const handleSaveMessage = () => {
    if (gymId) {
      updateMessage.mutate({ gymId, message: message.trim() });
    }
  };

  const totalReservations = reservations?.length || 0;
  const confirmedReservations = reservations?.filter(r => r.status === 'confirmed').length || 0;
  const pendingReservations = reservations?.filter(r => r.status === 'pending').length || 0;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <Badge variant="default" className="bg-green-500">Confirmada</Badge>;
      case 'pending':
        return <Badge variant="outline" className="border-yellow-500 text-yellow-600">Pendiente</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Cancelada</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return format(date, "PPP", { locale: es });
  };

  const handleViewReceipt = (receiptUrl: string, itemName: string, userName: string) => {
    setReceiptModal({
      isOpen: true,
      receiptUrl,
      title: `${itemName} - ${userName}`
    });
  };

  return (
    <div className="space-y-6">
      {/* Mensaje personalizado para reservas */}
      <Card>
        <CardHeader>
          <CardTitle>Mensaje para Reservas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">
              Mensaje que aparecerá en las reservas de los socios
            </label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Ej: Recuerda llegar 10 minutos antes y traer tu identificación"
              className="resize-none"
              rows={3}
            />
            <div className="flex justify-between items-center mt-2">
              <span className="text-xs text-muted-foreground">
                {message.length} caracteres
              </span>
              <Button 
                onClick={handleSaveMessage}
                disabled={updateMessage.isPending || message === currentMessage}
                size="sm"
              >
                {updateMessage.isPending ? 'Guardando...' : 'Guardar mensaje'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Reservas</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalReservations}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Confirmadas</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{confirmedReservations}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pendientes</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingReservations}</div>
          </CardContent>
        </Card>
      </div>

      {/* Reservations Section */}
      <Card>
        <CardHeader>
          <CardTitle>Todas las Reservas</CardTitle>
        </CardHeader>
        <CardContent>
          {reservations && reservations.length > 0 ? (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Usuario</TableHead>
                      <TableHead>Elemento</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Horario</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Precio</TableHead>
                      <TableHead>Comprobante</TableHead>
                      <TableHead>Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reservations.map((reservation) => (
                      <TableRow key={reservation.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">
                              {reservation.profiles?.first_name} {reservation.profiles?.last_name}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {reservation.profiles?.username}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {reservation.reservation_items?.name}
                        </TableCell>
                        <TableCell>
                          {formatDate(reservation.reservation_date)}
                        </TableCell>
                        <TableCell>
                          {reservation.start_time} - {reservation.end_time}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(reservation.status)}
                        </TableCell>
                        <TableCell>
                          ${reservation.total_price}
                        </TableCell>
                        <TableCell>
                          {reservation.payment_receipt_url ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewReceipt(
                                reservation.payment_receipt_url!,
                                reservation.reservation_items?.name || 'Reserva',
                                `${reservation.profiles?.first_name} ${reservation.profiles?.last_name}`
                              )}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          ) : (
                            <span className="text-sm text-muted-foreground">Sin comprobante</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {reservation.status !== 'cancelled' && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => setSelectedReservation(reservation)}
                                  disabled={cancelReservation.isPending}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Cancelar Reserva</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    ¿Estás seguro de que quieres cancelar la reserva de{' '}
                                    <strong>
                                      {reservation.profiles?.first_name} {reservation.profiles?.last_name}
                                    </strong>{' '}
                                    para <strong>{reservation.reservation_items?.name}</strong> el{' '}
                                    <strong>{formatDate(reservation.reservation_date)}</strong>?
                                    <br />
                                    Esta acción no se puede deshacer.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => {
                                      if (selectedReservation) {
                                        cancelReservation.mutate(selectedReservation.id);
                                        setSelectedReservation(null);
                                      }
                                    }}
                                    disabled={cancelReservation.isPending}
                                  >
                                    {cancelReservation.isPending ? 'Cancelando...' : 'Confirmar'}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden space-y-4">
                {reservations.map((reservation) => (
                  <Card key={reservation.id} className="p-4">
                    <div className="space-y-3">
                      {/* Header with user info and status */}
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold text-sm">
                            {reservation.profiles?.first_name} {reservation.profiles?.last_name}
                          </h4>
                          <p className="text-xs text-muted-foreground">
                            @{reservation.profiles?.username}
                          </p>
                        </div>
                        {getStatusBadge(reservation.status)}
                      </div>

                      {/* Reservation details */}
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-muted-foreground text-xs">Elemento</p>
                          <p className="font-medium">{reservation.reservation_items?.name}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs">Precio</p>
                          <p className="font-medium">${reservation.total_price}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs">Fecha</p>
                          <p className="font-medium">{formatDate(reservation.reservation_date)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs">Horario</p>
                          <p className="font-medium">{reservation.start_time} - {reservation.end_time}</p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex justify-between items-center pt-2 border-t">
                        <div className="text-xs text-muted-foreground">
                          {reservation.payment_receipt_url ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewReceipt(
                                reservation.payment_receipt_url!,
                                reservation.reservation_items?.name || 'Reserva',
                                `${reservation.profiles?.first_name} ${reservation.profiles?.last_name}`
                              )}
                              className="h-8 text-xs"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              Ver comprobante
                            </Button>
                          ) : (
                            <span className="text-muted-foreground">Sin comprobante</span>
                          )}
                        </div>
                        
                        {reservation.status !== 'cancelled' && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => setSelectedReservation(reservation)}
                                disabled={cancelReservation.isPending}
                                className="h-8"
                              >
                                <X className="h-3 w-3 mr-1" />
                                Cancelar
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Cancelar Reserva</AlertDialogTitle>
                                <AlertDialogDescription>
                                  ¿Estás seguro de que quieres cancelar la reserva de{' '}
                                  <strong>
                                    {reservation.profiles?.first_name} {reservation.profiles?.last_name}
                                  </strong>{' '}
                                  para <strong>{reservation.reservation_items?.name}</strong> el{' '}
                                  <strong>{formatDate(reservation.reservation_date)}</strong>?
                                  <br />
                                  Esta acción no se puede deshacer.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => {
                                    if (selectedReservation) {
                                      cancelReservation.mutate(selectedReservation.id);
                                      setSelectedReservation(null);
                                    }
                                  }}
                                  disabled={cancelReservation.isPending}
                                >
                                  {cancelReservation.isPending ? 'Cancelando...' : 'Confirmar'}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">
                No hay reservas registradas en este gimnasio aún.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Receipt viewing modal */}
      <ReservationReceiptModal
        isOpen={receiptModal.isOpen}
        onClose={() => setReceiptModal({ isOpen: false, receiptUrl: null, title: '' })}
        receiptUrl={receiptModal.receiptUrl}
        reservationTitle={receiptModal.title}
      />
    </div>
  );
};